import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ShopService } from 'src/app/services/shop/shop.service';
import { CashRegisterService } from 'src/app/services/cash-register/cash-register.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ComercioActivoService } from 'src/app/services/merchant/comercio-activo.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-shops-list',
  templateUrl: './shops-list.component.html',
  styleUrls: ['./shops-list.component.css']
})
export class ShopsListComponent implements OnInit {
  shops: any[] = [];
  shopsFiltered: any[] = [];
  shopsPaginated: any[] = [];
  loading: boolean = true;
  
  // Modal de detalles
  showModal: boolean = false;
  selectedShop: any = null;
  showResponsiblePopover: boolean = false;
  
  // Popover de acciones
  openPopoverId: string | null = null;
  
  // Modal de crear/editar
  showFormModal: boolean = false;
  isEditingShop: boolean = false;
  editingShopCode: string = '';
  currentStep: number = 1; // 1: Datos de tienda, 2: Personal
  formData = {
    code: '',
    name: '',
    description: '',
    short_name: '',
    address: '',
    register_clients: false,
    available_purchases: false,
    visibility: false,
    status: true
  };
  
  // Personal de la tienda (array)
  personal: any[] = [];
  currentPerson = {
    position: '',
    custom_position: '',
    name: '',
    lastname: '',
    phone: '',
    email: ''
  };
  
  // Personal en edición
  editingPersonal: any[] = [];
  editingPersonIndex: number | null = null;
  editingPersonInline: number | null = null; // Para edición inline en tabla
  tempPersonData: any = null; // Datos temporales durante edición inline
  
  // Paginación
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  
  // Math para template
  Math = Math;
  
  // Filtros
  filters = {
    status: 'all', // all, active, inactive
    searchTerm: ''
  };

  // Estadísticas
  stats = {
    total: 0,
    active: 0,
    inactive: 0,
    withRegistration: 0,
    withPurchases: 0
  };

  // Gestión de cajas
  showCashRegistersModal: boolean = false;
  selectedShopForCashRegisters: any = null;
  cashRegisters: any[] = [];
  loadingCashRegisters: boolean = false;
  
  // Modal de crear/editar caja
  showCashRegisterFormModal: boolean = false;
  isEditingCashRegister: boolean = false;
  editingCashRegisterId: string = '';
  cashRegisterFormData = {
    cash_register_number: null as number | null,
    afiliation_number: '',
    type: 'NORMAL',
    description: ''
  };

  constructor(
    private shopService: ShopService,
    private cashRegisterService: CashRegisterService,
    private router: Router,
    public _auth: AuthService,
    private _comercioActivo: ComercioActivoService
  ) { }

  ngOnInit(): void {
    this.loadShops();
    
    // Cerrar popover al hacer click fuera
    document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Si el click no es en un botón de popover o dentro del popover, cerrar
      if (!target.closest('.popover-trigger') && !target.closest('.popover-content')) {
        this.closePopover();
      }
    });
  }

  /**
   * Cargar todas las tiendas
   */
  loadShops = async () => {
    this.loading = true;
    try {
      const response = await this.shopService.getShops();
      
      console.log('Response from API:', response);
      
      // El backend puede devolver response.data o directamente un array
      if (response) {
        // Si response tiene una propiedad data, usarla
        if (response.data && Array.isArray(response.data)) {
          this.shops = response.data;
        }
        // Si response es directamente un array
        else if (Array.isArray(response)) {
          this.shops = response;
        }
        else if (response.shops && Array.isArray(response.shops)) {
          this.shops = response.shops;
        }
        else {
          console.warn('Unexpected response format:', response);
          this.shops = response.data || [];
        }
      
        // Ordenar por código de forma ascendente
        if (this.shops.length > 0) {
          this.shops.sort((a: any, b: any) => {
            return (a.code || '').toString().localeCompare((b.code || '').toString());
          });
        }

        this.calculateStats();
        this.applyFilters();
        
        console.log('Shops loaded:', this.shops.length);
      } else {
        this.shops = [];
        this.shopsFiltered = [];
      }
    } catch (error) {
      console.error('Error loading shops:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las tiendas',
        confirmButtonColor: '#01abce'
      });
      this.shops = [];
      this.shopsFiltered = [];
    } finally {
      this.loading = false;
    }
  };

  /**
   * Calcular estadísticas
   */
  calculateStats = () => {
    this.stats.total = this.shops.length;
    this.stats.active = this.shops.filter(shop => shop.status === true).length;
    this.stats.inactive = this.shops.filter(shop => shop.status === false).length;
    this.stats.withRegistration = this.shops.filter(shop => shop.register_clients === true).length;
    this.stats.withPurchases = this.shops.filter(shop => shop.available_purchases === true).length;
  };

  /**
   * Aplicar filtros
   */
  applyFilters = () => {
    let filtered = [...this.shops];

    // Filtro por estado
    if (this.filters.status !== 'all') {
      if (this.filters.status === 'active') {
        filtered = filtered.filter(shop => shop.status === true);
      } else if (this.filters.status === 'inactive') {
        filtered = filtered.filter(shop => shop.status === false);
      }
    }

    // Filtro por búsqueda mejorado
    if (this.filters.searchTerm.trim() !== '') {
      const searchTerm = this.filters.searchTerm.trim();
      const searchLower = searchTerm.toLowerCase();
      
      // Verificar si el término de búsqueda es numérico (búsqueda por código)
      const isNumericSearch = /^\d+$/.test(searchTerm);
      
      if (isNumericSearch) {
        // Búsqueda numérica: solo buscar en código con "contiene"
        filtered = filtered.filter(shop => {
          const code = shop.code?.toString() || '';
          // El código debe contener el término de búsqueda
          return code.includes(searchTerm);
        });
      } else {
        // Búsqueda de texto: buscar en todos los campos
        filtered = filtered.filter(shop => 
          shop.code?.toLowerCase().includes(searchLower) ||
          shop.name?.toLowerCase().includes(searchLower) ||
          shop.short_name?.toLowerCase().includes(searchLower) ||
          shop.address?.toLowerCase().includes(searchLower)
        );
      }
    }

    this.shopsFiltered = filtered;
    this.currentPage = 1; // Reset a la primera página
    this.updatePagination();
  };

  /**
   * Actualizar paginación
   */
  updatePagination = () => {
    this.totalPages = Math.ceil(this.shopsFiltered.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.shopsPaginated = this.shopsFiltered.slice(startIndex, endIndex);
  };

  /**
   * Ir a página siguiente
   */
  nextPage = () => {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  };

  /**
   * Ir a página anterior
   */
  previousPage = () => {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  };

  /**
   * Ir a página específica
   */
  goToPage = (page: number) => {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  };

  /**
   * Manejar cambio de filtro
   */
  onFilterChange = () => {
    this.applyFilters();
  };

  /**
   * Limpiar filtros
   */
  clearFilters = () => {
    this.filters = {
      status: 'all',
      searchTerm: ''
    };
    this.applyFilters();
  };

  /**
   * Verificar si hay filtros activos
   */
  hasActiveFilters = (): boolean => {
    return this.filters.status !== 'all' || this.filters.searchTerm.trim() !== '';
  };

  /**
   * Abrir modal con detalles de tienda
   */
  viewShopDetail = (shop: any) => {
    this.closePopover();
    this.selectedShop = shop;
    this.showModal = true;
  };

  /**
   * Cerrar modal
   */
  closeModal = () => {
    this.showModal = false;
    this.selectedShop = null;
    this.showResponsiblePopover = false;
  };

  /**
   * Toggle popover de responsable
   */
  toggleResponsiblePopover = () => {
    this.showResponsiblePopover = !this.showResponsiblePopover;
  };

  /**
   * Copiar al portapapeles
   */
  copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Copiado',
        text: `${fieldName} copiado al portapapeles`,
        confirmButtonColor: '#01abce',
        timer: 1500,
        showConfirmButton: false
      });
    }).catch(err => {
      console.error('Error al copiar:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar al portapapeles',
        confirmButtonColor: '#01abce'
      });
    });
  };

  /**
   * Toggle popover de acciones
   */
  togglePopover = (shopCode: string) => {
    if (this.openPopoverId === shopCode) {
      this.openPopoverId = null;
    } else {
      this.openPopoverId = shopCode;
    }
  };

  /**
   * Cerrar popover
   */
  closePopover = () => {
    this.openPopoverId = null;
  };

  /**
   * Verificar si popover está abierto
   */
  isPopoverOpen = (shopCode: string): boolean => {
    return this.openPopoverId === shopCode;
  };

  /**
   * Abrir modal para editar tienda
   */
  openEditModal = (shop: any) => {
    this.closePopover();
    this.isEditingShop = true;
    this.editingShopCode = shop.code;
    this.currentStep = 1;
    this.formData = {
      code: shop.code || '',
      name: shop.name || '',
      description: shop.description || '',
      short_name: shop.short_name || '',
      address: shop.address || '',
      register_clients: shop.register_clients || false,
      available_purchases: shop.available_purchases || false,
      visibility: shop.visibility || false,
      status: shop.status !== undefined ? shop.status : true
    };
    // Cargar personal existente
    this.editingPersonal = shop.personal ? [...shop.personal] : [];
    this.editingPersonIndex = null;
    this.currentPerson = {
      position: '',
      custom_position: '',
      name: '',
      lastname: '',
      phone: '',
      email: ''
    };
    this.showFormModal = true;
  };

  /**
   * Abrir modal para crear nueva tienda
   */
  createNewShop = () => {
    this.isEditingShop = false;
    this.editingShopCode = '';
    this.currentStep = 1;
    this.formData = {
      code: '',
      name: '',
      description: '',
      short_name: '',
      address: '',
      register_clients: false,
      available_purchases: false,
      visibility: false,
      status: true
    };
    this.personal = [];
    this.currentPerson = {
      position: '',
      custom_position: '',
      name: '',
      lastname: '',
      phone: '',
      email: ''
    };
    this.showFormModal = true;
  };

  /**
   * Cerrar modal de formulario
   */
  closeFormModal = () => {
    this.showFormModal = false;
    this.currentStep = 1;
  };

  /**
   * Ir al siguiente paso
   */
  nextStep = () => {
    // Validar paso 1
    if (this.currentStep === 1) {
      if (!this.formData.code || !this.formData.name || !this.formData.short_name || !this.formData.address) {
        Swal.fire({
          icon: 'error',
          title: 'Campos incompletos',
          text: 'Por favor completa todos los campos obligatorios (Código, Nombre, Nombre Corto y Dirección)',
          confirmButtonColor: '#01abce'
        });
        return;
      }
      this.currentStep = 2;
    }
  };

  /**
   * Volver al paso anterior
   */
  previousStep = () => {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  };

  /**
   * Validar teléfono (solo números, máximo 10 dígitos)
   */
  validatePhone = (event: any) => {
    const input = event.target as HTMLInputElement;
    // Remover cualquier caracter que no sea número
    input.value = input.value.replace(/\D/g, '');
    // Limitar a 10 dígitos
    if (input.value.length > 10) {
      input.value = input.value.slice(0, 10);
    }
    this.currentPerson.phone = input.value;
  };

  /**
   * Validar prefijo de teléfono venezolano
   */
  validatePhonePrefix = (phone: string): boolean => {
    if (!phone || phone.length !== 10) {
      return false;
    }
    const validPrefixes = ['412', '422', '424', '414', '426', '416', '212'];
    const prefix = phone.substring(0, 3);
    return validPrefixes.includes(prefix);
  };

  /**
   * Validar email
   */
  validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
    return emailRegex.test(email);
  };

  /**
   * Agregar persona al personal
   */
  addPerson = () => {
    // Validar nombre y apellido (obligatorios)
    if (!this.currentPerson.name || !this.currentPerson.lastname) {
      Swal.fire({
        icon: 'error',
        title: 'Campos incompletos',
        text: 'El nombre y apellido son obligatorios',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar cargo
    if (!this.currentPerson.position) {
      Swal.fire({
        icon: 'error',
        title: 'Campo incompleto',
        text: 'Debes seleccionar un cargo',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Si seleccionó "Otro", validar que ingresó un cargo personalizado
    if (this.currentPerson.position === 'Otro' && !this.currentPerson.custom_position) {
      Swal.fire({
        icon: 'error',
        title: 'Campo incompleto',
        text: 'Debes especificar el cargo personalizado',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar email si fue ingresado
    if (this.currentPerson.email && !this.validateEmail(this.currentPerson.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Email inválido',
        text: 'Por favor ingresa un correo electrónico válido',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar teléfono si fue ingresado
    if (this.currentPerson.phone) {
      if (this.currentPerson.phone.length !== 10) {
        Swal.fire({
          icon: 'error',
          title: 'Teléfono inválido',
          text: 'El teléfono debe tener exactamente 10 dígitos',
          confirmButtonColor: '#01abce'
        });
        return;
      }
      if (!this.validatePhonePrefix(this.currentPerson.phone)) {
        Swal.fire({
          icon: 'error',
          title: 'Teléfono inválido',
          text: 'El teléfono debe comenzar con: 412, 422, 424, 414, 426, 416 o 212',
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    // Validar email duplicado
    if (this.currentPerson.email) {
      const emailExists = this.personal.some(p => p.email?.toLowerCase() === this.currentPerson.email.toLowerCase());
      if (emailExists) {
        Swal.fire({
          icon: 'error',
          title: 'Email duplicado',
          text: 'Ya existe una persona con ese email en el personal',
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    // Agregar persona al array
    const finalPosition = this.currentPerson.position === 'Otro' 
      ? this.currentPerson.custom_position 
      : this.currentPerson.position;

    // Validar que no haya más de un Supervisor o Gerente
    if (finalPosition === 'Supervisor' || finalPosition === 'Gerente') {
      const existingPosition = this.personal.some(p => p.position === finalPosition);
      
      if (existingPosition) {
        Swal.fire({
          icon: 'error',
          title: 'Cargo duplicado',
          text: `Ya existe un ${finalPosition} en el personal. Solo puede haber uno.`,
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    this.personal.push({
      name: this.currentPerson.name,
      lastname: this.currentPerson.lastname,
      phone: this.currentPerson.phone || undefined,
      email: this.currentPerson.email || undefined,
      position: finalPosition
    });

    // Limpiar formulario
    this.currentPerson = {
      position: '',
      custom_position: '',
      name: '',
      lastname: '',
      phone: '',
      email: ''
    };

    Swal.fire({
      icon: 'success',
      title: 'Persona agregada',
      text: 'La persona ha sido agregada al personal',
      confirmButtonColor: '#01abce',
      timer: 1500,
      showConfirmButton: false
    });
  };

  /**
   * Eliminar persona del personal
   */
  removePerson = (index: number) => {
    this.personal.splice(index, 1);
  };

  /**
   * Agregar o actualizar persona en el personal de edición
   */
  addOrUpdateEditingPerson = () => {
    // Validar nombre y apellido (obligatorios)
    if (!this.currentPerson.name || !this.currentPerson.lastname) {
      Swal.fire({
        icon: 'error',
        title: 'Campos incompletos',
        text: 'El nombre y apellido son obligatorios',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar cargo
    if (!this.currentPerson.position) {
      Swal.fire({
        icon: 'error',
        title: 'Campo incompleto',
        text: 'Debes seleccionar un cargo',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Si seleccionó "Otro", validar que ingresó un cargo personalizado
    if (this.currentPerson.position === 'Otro' && !this.currentPerson.custom_position) {
      Swal.fire({
        icon: 'error',
        title: 'Campo incompleto',
        text: 'Debes especificar el cargo personalizado',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar email si fue ingresado
    if (this.currentPerson.email && !this.validateEmail(this.currentPerson.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Email inválido',
        text: 'Por favor ingresa un correo electrónico válido',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar teléfono si fue ingresado
    if (this.currentPerson.phone) {
      if (this.currentPerson.phone.length !== 10) {
        Swal.fire({
          icon: 'error',
          title: 'Teléfono inválido',
          text: 'El teléfono debe tener exactamente 10 dígitos',
          confirmButtonColor: '#01abce'
        });
        return;
      }
      if (!this.validatePhonePrefix(this.currentPerson.phone)) {
        Swal.fire({
          icon: 'error',
          title: 'Teléfono inválido',
          text: 'El teléfono debe comenzar con: 412, 422, 424, 414, 426, 416 o 212',
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    // Validar email duplicado (excepto si estamos editando la misma persona)
    if (this.currentPerson.email) {
      const emailExists = this.editingPersonal.some((p, index) => 
        index !== this.editingPersonIndex && 
        p.email?.toLowerCase() === this.currentPerson.email.toLowerCase()
      );
      if (emailExists) {
        Swal.fire({
          icon: 'error',
          title: 'Email duplicado',
          text: 'Ya existe una persona con ese email en el personal',
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    const finalPosition = this.currentPerson.position === 'Otro' 
      ? this.currentPerson.custom_position 
      : this.currentPerson.position;

    // Validar que no haya más de un Supervisor o Gerente
    if (finalPosition === 'Supervisor' || finalPosition === 'Gerente') {
      const existingPosition = this.editingPersonal.some((p, index) => 
        index !== this.editingPersonIndex && 
        p.position === finalPosition
      );
      
      if (existingPosition) {
        Swal.fire({
          icon: 'error',
          title: 'Cargo duplicado',
          text: `Ya existe un ${finalPosition} en el personal. Solo puede haber uno.`,
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    const personData = {
      name: this.currentPerson.name,
      lastname: this.currentPerson.lastname,
      phone: this.currentPerson.phone || undefined,
      email: this.currentPerson.email || undefined,
      position: finalPosition
    };

    if (this.editingPersonIndex !== null) {
      // Actualizar persona existente
      this.editingPersonal[this.editingPersonIndex] = personData;
      this.editingPersonIndex = null;
    } else {
      // Agregar nueva persona
      this.editingPersonal.push(personData);
    }

    // Limpiar formulario
    this.currentPerson = {
      position: '',
      custom_position: '',
      name: '',
      lastname: '',
      phone: '',
      email: ''
    };

    // NO mostrar alerta, solo agregar al array local
    // Los cambios se guardarán al hacer click en "Guardar Cambios"
  };

  /**
   * Editar una persona del personal
   */
  editEditingPerson = (index: number) => {
    const person = this.editingPersonal[index];
    this.editingPersonIndex = index;
    
    // Determinar si el cargo es personalizado
    const standardPositions = ['Supervisor', 'Gerente'];
    const isCustomPosition = !standardPositions.includes(person.position);
    
    this.currentPerson = {
      position: isCustomPosition ? 'Otro' : person.position,
      custom_position: isCustomPosition ? person.position : '',
      name: person.name,
      lastname: person.lastname,
      phone: person.phone || '',
      email: person.email || ''
    };
  };

  /**
   * Cancelar edición de persona
   */
  cancelEditingPerson = () => {
    this.editingPersonIndex = null;
    this.currentPerson = {
      position: '',
      custom_position: '',
      name: '',
      lastname: '',
      phone: '',
      email: ''
    };
  };

  /**
   * Eliminar persona del personal en edición
   */
  removeEditingPerson = async (index: number) => {
    // Confirmar eliminación
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar persona?',
      text: 'Los cambios se aplicarán al guardar',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Eliminar del array local
    this.editingPersonal.splice(index, 1);
    if (this.editingPersonIndex === index) {
      this.cancelEditingPerson();
    }
    if (this.editingPersonInline === index) {
      this.editingPersonInline = null;
      this.tempPersonData = null;
    }

    // NO guardar inmediatamente, solo eliminar del array local
    // Los cambios se guardarán al hacer click en "Guardar Cambios"
  };

  /**
   * Iniciar edición inline en la tabla
   */
  startInlineEdit = (index: number) => {
    this.editingPersonInline = index;
    // Guardar copia de los datos originales
    this.tempPersonData = { ...this.editingPersonal[index] };
  };

  /**
   * Confirmar edición inline
   */
  confirmInlineEdit = (index: number) => {
    // Validar datos
    const person = this.tempPersonData;
    
    if (!person.name || !person.lastname) {
      Swal.fire({
        icon: 'error',
        title: 'Campos incompletos',
        text: 'El nombre y apellido son obligatorios',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    if (!person.position) {
      Swal.fire({
        icon: 'error',
        title: 'Campo incompleto',
        text: 'El cargo es obligatorio',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar email si fue ingresado
    if (person.email && !this.validateEmail(person.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Email inválido',
        text: 'Por favor ingresa un correo electrónico válido',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    // Validar teléfono si fue ingresado
    if (person.phone) {
      if (person.phone.length !== 10) {
        Swal.fire({
          icon: 'error',
          title: 'Teléfono inválido',
          text: 'El teléfono debe tener exactamente 10 dígitos',
          confirmButtonColor: '#01abce'
        });
        return;
      }
      if (!this.validatePhonePrefix(person.phone)) {
        Swal.fire({
          icon: 'error',
          title: 'Teléfono inválido',
          text: 'El teléfono debe comenzar con: 412, 422, 424, 414, 426, 416 o 212',
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    // Validar que no haya más de un Supervisor o Gerente
    if (person.position === 'Supervisor' || person.position === 'Gerente') {
      const existingPosition = this.editingPersonal.some((p, i) => 
        i !== index && 
        p.position === person.position
      );
      
      if (existingPosition) {
        Swal.fire({
          icon: 'error',
          title: 'Cargo duplicado',
          text: `Ya existe un ${person.position} en el personal. Solo puede haber uno.`,
          confirmButtonColor: '#01abce'
        });
        return;
      }
    }

    // Actualizar en el array
    this.editingPersonal[index] = { ...this.tempPersonData };
    this.editingPersonInline = null;
    this.tempPersonData = null;
  };

  /**
   * Cancelar edición inline
   */
  cancelInlineEdit = () => {
    this.editingPersonInline = null;
    this.tempPersonData = null;
  };

  /**
   * Scroll a la sección de gestión de personal
   */
  scrollToPersonalManagement = () => {
    const element = document.getElementById('personal-management-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /**
   * Guardar tienda (crear o editar)
   */
  saveShop = async () => {
    // No hay validaciones obligatorias para el personal (es opcional)

    try {
      let response;
      
      if (this.isEditingShop) {
        // Editar tienda existente
        const updateData: any = { ...this.formData };
        delete updateData.code; // No se puede actualizar el código
        
        response = await this.shopService.updateShop(this.editingShopCode, updateData);
        
        // Actualizar personal (se guarda todo al final)
        await this.shopService.updateShopPersonal(this.editingShopCode, this.editingPersonal);
        
        if (response) {
          Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: 'Tienda y personal actualizados correctamente',
            confirmButtonColor: '#01abce',
            timer: 2000
          });
        }
      } else {
        // Crear nueva tienda con personal
        // Con un comercio elegido arriba, la tienda nace de ese comercio. Sin
        // esto quedaba siempre de MundoTotal: la API toma el comercio del
        // cuerpo, no del `?merchant=` que agrega el interceptor.
        const comercio = this._comercioActivo.codigo;
        const shopData = {
          ...this.formData,
          personal: this.personal,
          ...(comercio ? { merchant: comercio } : {})
        };
        
        response = await this.shopService.createShop(shopData);
        
        if (response) {
          Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: 'Tienda creada correctamente',
            confirmButtonColor: '#01abce',
            timer: 2000
          });
        }
      }
      
      this.closeFormModal();
      await this.loadShops();
    } catch (error: any) {
      console.error('Error saving shop:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.error?.msg || `No se pudo ${this.isEditingShop ? 'actualizar' : 'crear'} la tienda`,
        confirmButtonColor: '#01abce'
      });
    }
  };

  /**
   * Cambiar estado de tienda (activar/inactivar)
   */
  toggleShopStatus = async (shop: any, event: Event) => {
    event.stopPropagation();
    
    const currentStatus = shop.status;
    const actionText = currentStatus ? 'inactivar' : 'activar';
    
    const result = await Swal.fire({
      title: `¿${actionText.charAt(0).toUpperCase() + actionText.slice(1)} tienda?`,
      text: `¿Estás seguro de que deseas ${actionText} la tienda "${shop.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#01abce',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Sí, ${actionText}`,
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await this.shopService.toggleShopStatus(shop.code);
        
        if (response && response.shop) {
          Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: response.msg || `Tienda ${response.newStatus ? 'activada' : 'inactivada'} correctamente`,
            confirmButtonColor: '#01abce',
            timer: 2000
          });

          // Recargar tiendas
          await this.loadShops();
        } else {
          throw new Error('Respuesta inválida del servidor');
        }
      } catch (error) {
        console.error('Error toggling shop status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cambiar el estado de la tienda',
          confirmButtonColor: '#01abce'
        });
      }
    }
  };

  /**
   * Obtener clase de badge según estado
   */
  getStatusBadgeClass = (status: boolean): string => {
    return status ? 'badge-success' : 'badge-secondary';
  };

  /**
   * Obtener texto de estado
   */
  getStatusText = (status: boolean): string => {
    return status ? 'Activa' : 'Inactiva';
  };

  // ========================================
  // MÉTODOS DE GESTIÓN DE CAJAS
  // ========================================

  openCashRegistersModal(shop: any): void {
    this.selectedShopForCashRegisters = shop;
    this.showCashRegistersModal = true;
    this.loadCashRegisters(shop.code);
    this.closePopover();
  }

  loadCashRegisters(shopCode: string): void {
    this.loadingCashRegisters = true;
    this.cashRegisterService.getCashRegistersByShop(shopCode).subscribe({
      next: (response) => {
        this.cashRegisters = response.cashRegisters || [];
        this.loadingCashRegisters = false;
      },
      error: (error) => {
        console.error('Error loading cash registers:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las cajas',
          confirmButtonColor: '#01abce'
        });
        this.loadingCashRegisters = false;
      }
    });
  }

  createNewCashRegister(): void {
    this.isEditingCashRegister = false;
    this.cashRegisterFormData = {
      cash_register_number: null,
      afiliation_number: '',
      type: 'NORMAL',
      description: ''
    };
    this.showCashRegisterFormModal = true;
  }

  editCashRegister(cashRegister: any): void {
    this.isEditingCashRegister = true;
    this.editingCashRegisterId = cashRegister._id;
    this.cashRegisterFormData = {
      cash_register_number: cashRegister.cash_register_number,
      afiliation_number: cashRegister.afiliation_number,
      type: cashRegister.type,
      description: cashRegister.description || ''
    };
    this.showCashRegisterFormModal = true;
  }

  saveCashRegister(): void {
    if (!this.cashRegisterFormData.cash_register_number || !this.cashRegisterFormData.afiliation_number) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: 'El número de caja y el número de afiliación son obligatorios',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    if (this.isEditingCashRegister) {
      this.cashRegisterService.updateCashRegister(this.editingCashRegisterId, this.cashRegisterFormData).subscribe({
        next: (response) => {
          this.showCashRegisterFormModal = false;
          this.loadCashRegisters(this.selectedShopForCashRegisters.code);
          
          if (response.warning) {
            Swal.fire({
              icon: 'warning',
              title: '⚠️ Advertencia',
              html: `<div style="text-align: left;">
                <p><strong>Caja actualizada exitosamente</strong></p>
                <hr style="margin: 10px 0;">
                <p style="color: #856404;">${response.warning.msg}</p>
              </div>`,
              confirmButtonColor: '#01abce',
              confirmButtonText: 'Entendido'
            });
          } else {
            Swal.fire({
              icon: 'success',
              title: 'Éxito',
              text: 'Caja actualizada correctamente',
              confirmButtonColor: '#01abce',
              timer: 2000
            });
          }
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.error?.error || error.error?.msg || 'Error al actualizar caja',
            confirmButtonColor: '#01abce'
          });
        }
      });
    } else {
      const data = {
        ...this.cashRegisterFormData,
        shop_code: this.selectedShopForCashRegisters.code
      };
      this.cashRegisterService.createCashRegister(data).subscribe({
        next: (response) => {
          this.showCashRegisterFormModal = false;
          this.loadCashRegisters(this.selectedShopForCashRegisters.code);
          
          if (response.warning) {
            Swal.fire({
              icon: 'warning',
              title: '⚠️ Advertencia',
              html: `<div style="text-align: left;">
                <p><strong>Caja creada exitosamente</strong></p>
                <hr style="margin: 10px 0;">
                <p style="color: #856404;">${response.warning.msg}</p>
              </div>`,
              confirmButtonColor: '#01abce',
              confirmButtonText: 'Entendido'
            });
          } else {
            Swal.fire({
              icon: 'success',
              title: 'Éxito',
              text: 'Caja creada correctamente',
              confirmButtonColor: '#01abce',
              timer: 2000
            });
          }
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.error?.error || error.error?.msg || 'Error al crear caja',
            confirmButtonColor: '#01abce'
          });
        }
      });
    }
  }

  deleteCashRegister(cashRegister: any): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: `Se eliminará la caja #${cashRegister.cash_register_number}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#01abce',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.cashRegisterService.deleteCashRegister(cashRegister._id).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: 'Eliminado',
              text: 'Caja eliminada correctamente',
              confirmButtonColor: '#01abce',
              timer: 2000
            });
            this.loadCashRegisters(this.selectedShopForCashRegisters.code);
          },
          error: (error) => {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'No se pudo eliminar la caja',
              confirmButtonColor: '#01abce'
            });
          }
        });
      }
    });
  }

  closeCashRegistersModal(): void {
    this.showCashRegistersModal = false;
    this.selectedShopForCashRegisters = null;
    this.cashRegisters = [];
  }
}
